import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, OverlayView, Polygon } from '@react-google-maps/api';
import { useCompany } from '@core/utils/hooks/misc';
import { GoogleMapsLoader } from './GoogleMapsLoader';
import { labelPoint, LatLng } from './geoJson';

export interface MapTerritory {
  id: string;
  name: string;
  color: string;
  paths: LatLng[][];
  reps: string[];
  openDeals?: number;
}

export interface TerritoryMapProps {
  territories: MapTerritory[];
  selectedId: string | null;
  /** Territories drawn as active when nothing is selected, e.g. a deal's match or its overlap candidates. */
  highlightIds?: string[];
  /** A location to pin; the map then frames the pin and the highlighted territories. */
  pin?: LatLng | null;
  /** Bump version to fit the map to that territory, or to every territory when id is null. */
  focus: { id: string | null; version: number };
  onSelect?(id: string | null): void;
  height: number | string;
}

const NO_IDS: string[] = [];

const MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];
const MAP_OPTIONS = { styles: MAP_STYLES, clickableIcons: false, streetViewControl: false, mapTypeControl: false };

/** Read-only map of every territory: coloured boundaries with a name and representatives label each. */
export const TerritoryMap = memo((props: TerritoryMapProps) => <GoogleMapsLoader>{() => <MapView {...props} />}</GoogleMapsLoader>);

const MapView = memo(({ territories, selectedId, highlightIds = NO_IDS, pin = null, focus, onSelect, height }: TerritoryMapProps) => {
  const company = useCompany();
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const countryCode = company?.countryCode;
  const defaultCenter = useMemo(
    () => ((countryCode || '').toUpperCase() === 'CA' ? { lat: 56.13, lng: -106.35 } : { lat: 39.5, lng: -98.35 }),
    [countryCode],
  );

  const fit = useCallback(
    (id: string | null) => {
      if (!map) return;
      const points = territories
        .filter(t => !id || t.id === id)
        .reduce((all, t) => t.paths.reduce((acc, path) => acc.concat(path), all), [] as LatLng[]);
      if (!points.length) return;
      const bounds = new google.maps.LatLngBounds();
      points.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, 60);
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() || 0) > 12) map.setZoom(12);
      });
    },
    [map, territories],
  );

  const activeIds = useMemo(() => (selectedId ? [selectedId] : highlightIds), [highlightIds, selectedId]);
  const select = useCallback((id: string | null) => onSelect && onSelect(id), [onSelect]);

  const fitPin = useCallback(() => {
    if (!map || !pin) return;
    const points = territories
      .filter(t => activeIds.includes(t.id))
      .reduce((all, t) => t.paths.reduce((acc, path) => acc.concat(path), all), [pin] as LatLng[]);
    if (points.length === 1) {
      map.setCenter(pin);
      map.setZoom(11);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach(p => bounds.extend(p));
    map.fitBounds(bounds, 60);
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() || 0) > 13) map.setZoom(13);
    });
  }, [activeIds, map, pin, territories]);

  // Fit once the map and the territories are both in (to the focused territory
  // when the map remounts after an edit), then on every focus request. A pinned
  // map frames its pin instead, and again whenever the pin or highlight moves.
  const hasShapes = territories.some(t => t.paths.length);
  const pinKey = pin ? `${pin.lat},${pin.lng}|${activeIds.join(',')}` : '';
  useEffect(() => {
    if (map && pin) fitPin();
    else if (map && hasShapes) fit(focus.version ? focus.id : null);
  }, [map, hasShapes, pinKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focus.version) fit(focus.id);
  }, [focus]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GoogleMap
      mapContainerStyle={{ height, width: '100%', borderRadius: 4 }}
      center={defaultCenter}
      zoom={4}
      options={MAP_OPTIONS}
      onLoad={setMap}
      onUnmount={() => setMap(null)}
      onClick={() => select(null)}
    >
      {territories.map(t => {
        const active = activeIds.includes(t.id);
        const dimmed = !!activeIds.length && !active;
        const options = {
          fillColor: t.color,
          strokeColor: t.color,
          fillOpacity: active ? 0.35 : dimmed ? 0.1 : 0.2,
          strokeOpacity: dimmed ? 0.5 : 0.9,
          strokeWeight: active ? 3 : dimmed ? 1 : 2,
          zIndex: active ? 3 : 1,
        };
        return t.paths.map((path, i) => <Polygon key={`${t.id}-${i}`} paths={path} options={options} onClick={() => select(t.id)} />);
      })}
      {territories.map(t => {
        const position = labelPoint(t.paths);
        if (!position) return null;
        return (
          <OverlayView
            key={`label-${t.id}`}
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
          >
            <TerritoryLabel
              territory={t}
              active={activeIds.includes(t.id)}
              dimmed={!!activeIds.length && !activeIds.includes(t.id)}
              onSelect={select}
            />
          </OverlayView>
        );
      })}
      {pin && <Marker position={pin} zIndex={10} />}
    </GoogleMap>
  );
});

function repSummary(reps: string[]) {
  if (!reps.length) return 'No representatives';
  if (reps.length <= 2) return reps.join(', ');
  return `${reps.slice(0, 2).join(', ')} +${reps.length - 2}`;
}

const TerritoryLabel = memo(
  ({ territory, active, dimmed, onSelect }: { territory: MapTerritory; active: boolean; dimmed: boolean; onSelect(id: string): void }) => {
    // Without this a click on the label also reaches the map, which clears the selection.
    const ref = useCallback((el: HTMLDivElement | null) => {
      if (el) google.maps.OverlayView.preventMapHitsAndGesturesFrom(el);
    }, []);
    return (
      <div
        ref={ref}
        onClick={() => onSelect(territory.id)}
        style={{
          width: 'max-content',
          background: '#fff',
          border: `${active ? 2 : 1}px solid ${territory.color}`,
          borderRadius: 12,
          padding: '3px 10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          opacity: dimmed ? 0.65 : 1,
          lineHeight: 1.3,
          color: '#343a40',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: territory.color, marginRight: 6 }} />
          {territory.name}
        </div>
        <div style={{ fontSize: 11, color: '#6c757d' }}>{repSummary(territory.reps)}</div>
      </div>
    );
  },
);
